import $ from 'jquery';
import { profileResultsLink } from '../utils/profile_filter_context';

if (!window.$ || !window.jQuery) {
  window.$ = window.jQuery = $;
}

const googleMapsApiReady = () => Boolean(
  window.google && window.google.maps && window.google.maps.places
);
const GOOGLE_MAPS_API_TIMEOUT_MS = 15000;
const GOOGLE_MAPS_API_TERMINAL_TIMEOUT_MS = 60000;
let mapResultsStorageType = null;

window.loadGoogleMapsApi = () => {
  if (googleMapsApiReady()) return Promise.resolve(window.google);
  if (window.googleMapsApiTerminalFailure) {
    return Promise.reject(window.googleMapsApiTerminalFailure);
  }
  if (!window.googleMapsApiPromise) {
    const keyElement = document.querySelector('meta[name="google-maps-api-key"]');
    const apiKey = keyElement && keyElement.content;
    if (!apiKey) return Promise.reject(new Error('Google Maps API key is unavailable.'));

    const request = new Promise((resolve, reject) => {
      const attemptId = (window.googleMapsApiAttempt || 0) + 1;
      window.googleMapsApiAttempt = attemptId;
      const callbackName = `__googleMapsApiLoaded_${attemptId}`;
      const existingScript = document.querySelector('script[data-google-maps-api]');
      if (existingScript) existingScript.remove();

      let script;
      let terminalTimeoutId;
      let settled = false;
      const settle = (callback, value, removeScript = false, preserveCallback = false) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(terminalTimeoutId);
        if (preserveCallback) {
          const ignoreLateCallback = () => {
            if (window[callbackName] === ignoreLateCallback) delete window[callbackName];
          };
          window[callbackName] = ignoreLateCallback;
          window.setTimeout(ignoreLateCallback, GOOGLE_MAPS_API_TERMINAL_TIMEOUT_MS);
        } else {
          delete window[callbackName];
        }
        if (removeScript && script) script.remove();
        callback(value);
      };
      const handleReady = () => {
        if (window.googleMapsApiAttempt !== attemptId) {
          delete window[callbackName];
          if (script) script.remove();
          return;
        }
        if (googleMapsApiReady()) {
          settle(resolve, window.google);
        } else {
          settle(reject, new Error('Google Maps API loaded without Places support.'), true);
        }
      };
      const handleError = () => {
        settle(reject, new Error('Google Maps API failed to load.'), true);
      };
      window[callbackName] = handleReady;

      const params = new URLSearchParams({
        key: apiKey,
        libraries: 'places',
        language: 'en',
        region: 'US',
        loading: 'async',
        callback: callbackName
      });
      script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.dataset.googleMapsApi = 'true';
      script.addEventListener('error', handleError, { once: true });
      terminalTimeoutId = window.setTimeout(() => {
        const error = new Error('Google Maps API request reached its terminal timeout. Reload to retry.');
        window.googleMapsApiTerminalFailure = error;
        settle(reject, error, true, true);
      }, GOOGLE_MAPS_API_TERMINAL_TIMEOUT_MS);
      document.head.appendChild(script);
    });

    let sharedRequest;
    sharedRequest = request.finally(() => {
      if (window.googleMapsApiPromise === sharedRequest && !window.googleMapsApiTerminalFailure) {
        window.googleMapsApiPromise = null;
      }
    });
    window.googleMapsApiPromise = sharedRequest;
  }

  let timeoutId;
  const timeout = new Promise((resolve, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error('Google Maps API request timed out.'));
    }, GOOGLE_MAPS_API_TIMEOUT_MS);
  });
  return Promise.race([window.googleMapsApiPromise, timeout])
    .finally(() => window.clearTimeout(timeoutId));
};

window.lastMarker;
window.storage_type;
window.markers = [];
window.mapInitialized = false;
window.mapMarkersDirty = false;
window.pendingMapInitialization = false;
window.mapInstance = null;
window.mapLastCenter = null;
window.mapLastScrollY = 0;
window.mapScrollGuardHandlers = [];
window.mapResizeHandler = null;
window.mapLoadingHidden = false;
window.mapLoadingFallbackTimer = null;

const showMapLoadingOverlay = () => {
  const overlay = document.getElementById('map-loading-overlay');
  if (overlay) {
    overlay.classList.add('visible');
    window.mapLoadingHidden = false;
  }
};

const hideMapLoadingOverlay = () => {
  if (window.mapLoadingHidden) return;
  if (window.mapLoadingFallbackTimer) {
    window.clearTimeout(window.mapLoadingFallbackTimer);
    window.mapLoadingFallbackTimer = null;
  }
  const overlay = document.getElementById('map-loading-overlay');
  if (overlay) {
    overlay.classList.remove('visible');
  }
  window.mapLoadingHidden = true;
};

const updateMapModalHeight = () => {
  const modal = document.getElementById('cmprPriceModal');
  const mapElement = document.getElementById('map');
  if (!modal || !mapElement) return;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  if (!viewportHeight) return;
  const heightValue = `${viewportHeight}px`;
  modal.style.height = heightValue;
  modal.style.minHeight = heightValue;
  mapElement.style.height = heightValue;
  mapElement.style.minHeight = heightValue;
};

const resetMapModalHeight = () => {
  const modal = document.getElementById('cmprPriceModal');
  const mapElement = document.getElementById('map');
  if (modal) {
    modal.style.height = '';
    modal.style.minHeight = '';
  }
  if (mapElement) {
    mapElement.style.height = '';
    mapElement.style.minHeight = '';
  }
};

const forceScrollTop = () => {
  try {
    window.scrollTo({ top: 0, behavior: 'auto' });
  } catch (error) {
    window.scrollTo(0, 0);
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const attachScrollGuard = () => {
  if (window.mapScrollGuardHandlers.length > 0) return;
  const scrollHandler = () => {
    if (!document.body.classList.contains('map-open')) return;
    if (window.scrollY !== 0 || document.documentElement.scrollTop !== 0) {
      forceScrollTop();
    }
  };
  const blockHandler = (event) => {
    if (!document.body.classList.contains('map-open')) return;
    let target = event.target;
    if (target && target.nodeType !== 1) {
      target = target.parentElement;
    }
    const insideInfoWindow = target && (target.closest('.gm-style-iw') || target.closest('#infowindow-content'));
    if (insideInfoWindow) {
      return;
    }
    event.preventDefault();
    forceScrollTop();
  };
  window.mapScrollGuardHandlers = [
    { type: 'scroll', handler: scrollHandler, options: { passive: true } },
    { type: 'wheel', handler: blockHandler, options: { passive: false } },
    { type: 'touchmove', handler: blockHandler, options: { passive: false } }
  ];
  window.mapScrollGuardHandlers.forEach(({ type, handler, options }) => {
    window.addEventListener(type, handler, options);
  });
};

const removeScrollGuard = () => {
  if (window.mapScrollGuardHandlers.length === 0) return;
  window.mapScrollGuardHandlers.forEach(({ type, handler, options }) => {
    window.removeEventListener(type, handler, options);
  });
  window.mapScrollGuardHandlers = [];
};

const attachResizeHandler = () => {
  if (window.mapResizeHandler) return;
  window.mapResizeHandler = () => {
    if (!document.body.classList.contains('map-open')) return;
    updateMapModalHeight();
    if (window.mapInstance && window.google && window.google.maps) {
      google.maps.event.trigger(window.mapInstance, 'resize');
    }
  };
  window.addEventListener('resize', window.mapResizeHandler);
};

const removeResizeHandler = () => {
  if (!window.mapResizeHandler) return;
  window.removeEventListener('resize', window.mapResizeHandler);
  window.mapResizeHandler = null;
};

const hideCloseButton = () => {
  const button = document.querySelector('#close-infowindow-button');
  if (button) {
    button.style.display = 'none';
  }
};

var componentForm = {
    street_number: 'short_name',
    route: 'long_name',
    locality: 'long_name',
    administrative_area_level_1: 'short_name',
    country: 'long_name',
    postal_code: 'short_name'
};

var searchComponentForm = {
    street_number: 'short_name',
    route: 'long_name',
    locality: 'long_name',
    administrative_area_level_3: 'long_name',
    administrative_area_level_1: 'long_name',
    neighborhood: 'long_name',
    sublocality: 'long_name',
    sublocality_level_1: 'long_name',
    sublocality_level_2: 'long_name',
    sublocality_level_3: 'long_name',
    sublocality_level_4: 'long_name',
    sublocality_level_5: 'long_name',
    country: 'long_name',
    postal_code: 'short_name'
};

const initializeMapIfMarkersReady = () => {
  if (!Array.isArray(markers) || markers.length === 0) {
    return false;
  }
  setMapAndMarkers();
  return true;
};

const resetInitializedMap = () => {
  if (window.activeInfoWindow) {
    window.activeInfoWindow.close();
    window.activeInfoWindow = null;
  }
  hideCloseButton();
  window.mapInitialized = false;
  window.mapInstance = null;
  window.mapLastCenter = null;
  const mapElement = document.getElementById('map');
  if (mapElement) mapElement.innerHTML = '';
};

const notifyMarkersReady = () => {
  if (window.mapInitialized) {
    window.mapMarkersDirty = true;
    if (document.querySelector('#cmprPriceModal.open-map')) {
      requestMapInitialization();
    }
    return;
  }
  if (window.pendingMapInitialization) {
    if (initializeMapIfMarkersReady()) {
      window.pendingMapInitialization = false;
    }
  }
};

const requestMapInitialization = () => {
  if (!googleMapsApiReady()) {
    window.pendingMapInitialization = true;
    window.loadGoogleMapsApi()
      .then(requestMapInitialization)
      .catch((error) => {
        window.pendingMapInitialization = false;
        closeMapModal();
        console.error('Unable to load Google Maps.', error);
      });
    return;
  }
  if (window.mapInitialized && window.mapMarkersDirty) {
    resetInitializedMap();
  }
  if (window.mapInitialized) {
    if (window.mapInstance && window.google && window.google.maps) {
      setTimeout(() => {
        google.maps.event.trigger(window.mapInstance, 'resize');
        if (window.mapLastCenter) {
          window.mapInstance.setCenter(window.mapLastCenter);
        }
        hideMapLoadingOverlay();
      }, 150);
    }
    return;
  }
  if (!Array.isArray(markers) || markers.length === 0) {
    window.pendingMapInitialization = false;
    hideMapLoadingOverlay();
    return;
  }
  if (!initializeMapIfMarkersReady()) {
    window.pendingMapInitialization = true;
  }
};

const scrollWindowTo = (position) => {
  if (typeof window.scrollTo !== 'function') return;
  try {
    window.scrollTo({ top: position, behavior: 'auto' });
  } catch (error) {
    window.scrollTo(0, position);
  }
};

const lockBodyScroll = () => {
  window.mapLastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.top = `-${window.mapLastScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
  document.body.style.height = '100vh';
  document.documentElement.style.position = 'fixed';
  document.documentElement.style.width = '100%';
  document.documentElement.style.top = '0';
  document.documentElement.style.left = '0';
  document.documentElement.style.right = '0';
  document.documentElement.style.height = '100vh';
  document.documentElement.style.overflow = 'hidden';
  document.body.classList.add('map-open');
  document.documentElement.classList.add('map-open');
  forceScrollTop();
  requestAnimationFrame(forceScrollTop);
  setTimeout(forceScrollTop, 50);
  updateMapModalHeight();
  attachResizeHandler();
  attachScrollGuard();
};

const unlockBodyScroll = () => {
  const scrollY = window.mapLastScrollY || 0;
  removeScrollGuard();
  removeResizeHandler();
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  document.body.style.height = '';
  document.documentElement.style.position = '';
  document.documentElement.style.width = '';
  document.documentElement.style.top = '';
  document.documentElement.style.left = '';
  document.documentElement.style.right = '';
  document.documentElement.style.height = '';
  document.documentElement.style.overflow = '';
  document.body.classList.remove('map-open');
  document.documentElement.classList.remove('map-open');
  resetMapModalHeight();
  window.scrollTo(0, scrollY);
};

const openMapModal = () => {
  const $modal = $("#cmprPriceModal");
  if ($modal.length === 0) return;

  $modal.addClass('open-map');
  showMapLoadingOverlay();
  lockBodyScroll();
  requestMapInitialization();
};

const closeMapModal = () => {
  const $modal = $("#cmprPriceModal");
  if ($modal.length === 0) return;
  if (!$modal.hasClass('open-map')) return;

  $modal.removeClass('open-map');
  hideMapLoadingOverlay();
  unlockBodyScroll();
};

window.fetchMapMarkers = (city, state, zip_code, amenity, storage_type, neighborhood, unit_size, size_slug) => {
  const sizeSlugField = typeof document !== 'undefined' && document.getElementById('size_slug_param');
  size_slug = size_slug || (sizeSlugField && sizeSlugField.value);
  const params = new URLSearchParams({ city, state, zip_code: zip_code || '', storage_type });
  if (amenity) params.set('amenity', amenity);
  if (neighborhood) params.set('neighborhood', neighborhood);
  if (unit_size) params.set('unit_size', unit_size);
  if (size_slug) params.set('size_slug', size_slug);
  let actionUrl = '/map_markers.json?' + params.toString();

  fetch(actionUrl, {
    method: 'GET',
    credentials: 'same-origin'
  }).then((response) => {
    if (!response.ok) throw new Error(`Map markers request failed with status ${response.status}`);
    return response.json();
  })
  .then(data => {
    markers = Array.isArray(data) ? data : [];
    mapResultsStorageType = storage_type;
    if (lastMarker) markers.push(lastMarker);
    notifyMarkersReady();
  })
  .catch(error => {
    window.pendingMapInitialization = false;
    hideMapLoadingOverlay();
    console.error('Unable to load map markers.', error);
  });
}

window.fetchFacilityMarker = (facility_id, storage_type) => {
  let params = `?facility_id=${facility_id}&storage_type=${storage_type}`;
  let actionUrl = '/facility_marker.json' + params;

  fetch(actionUrl, {
    method: 'GET',
    credentials: 'same-origin'
  }).then((response) => response.json())
  .then(data => {
    markers.push(data);
    notifyMarkersReady();
  });
}

function backToListViewControl(controlDiv, map) {
  // Set CSS for the control border.
  const controlUI = document.createElement("div");

  controlUI.classList.add('close-map-view');
  controlUI.title = "Click to go back to list";
  controlDiv.appendChild(controlUI);

  // Set CSS for the control interior.
  const controlText = document.createElement("div");

  controlText.classList.add('close-map-view-text');
  controlText.innerHTML = "< Back to list view";
  controlUI.appendChild(controlText);
  // Setup the click event listeners: simply set the map to Chicago.
  controlUI.addEventListener("click", () => {
    closeMapModal();
  });
}

function clearStructuredPlaceFields(form) {
    if (!form) return;
    ['city', 'state', 'zip_code', 'neighborhood', 'query', 'kind'].forEach(function(name) {
        var field = form.querySelector('[name="search[place][' + name + ']"]');
        if (field) field.value = '';
    });
}

function searchAutocompleteType(value) {
    return /^\d{1,5}$/.test(value.trim()) ? 'postal_code' : 'geocode';
}

function updateSearchAutocompleteType(input) {
    var type = searchAutocompleteType(input.value);
    if (input.googlePlacesAutocomplete && input.googlePlacesAutocompleteType !== type) {
        input.googlePlacesAutocomplete.setTypes([type]);
        input.googlePlacesAutocompleteType = type;
    }
}

function setupLocationSearch(input) {
    if (!input || input.locationSearchHandlersAttached) return;
    input.locationSearchHandlersAttached = true;
    input.addEventListener('input', function() {
        clearStructuredPlaceFields(input.form);
        updateSearchAutocompleteType(input);
        if (input.value.trim().length > 0) loadSearchAutocomplete(input);
    }, true);

    if (input.form && !input.form.locationSearchSubmitHandlerAttached) {
        input.form.locationSearchSubmitHandlerAttached = true;
        input.form.addEventListener('submit', function(event) {
            if (input.value.trim().length === 0) event.preventDefault();
        });
    }
}

function initializeAutocomplete(input = document.getElementById('search_address')) {
    var type = searchAutocompleteType(input ? input.value : '');
    var options = {
        types: [type],
        fields: ["geometry", "name", "types", "formatted_address", "address_components"],
        componentRestrictions: {
            country: ["us"]
        }
    };
    if(input && !input.googlePlacesAutocomplete){
        var autocomplete = new google.maps.places.Autocomplete(input, options);
        input.googlePlacesAutocomplete = autocomplete;
        input.googlePlacesAutocompleteType = type;
        google.maps.event.addListener(autocomplete, 'place_changed', function() {
            var place = autocomplete.getPlace();
            if (!place || !Array.isArray(place.address_components)) return;

            var values = { city: '', state: '', zip_code: '', neighborhood: '' };
            var cityRank = 0;
            var neighborhoodRank = 0;
            var hasNeighborhoodComponent = false;
            place.address_components.forEach(function(component) {
                (component.types || []).forEach(function(addressType) {
                    var value = component[searchComponentForm[addressType]];
                    if (!value) return;
                    if (addressType === 'locality' && cityRank < 2) {
                        values.city = value;
                        cityRank = 2;
                    }
                    if (addressType === 'administrative_area_level_3' && cityRank < 1) {
                        values.city = value;
                        cityRank = 1;
                    }
                    if (addressType === 'administrative_area_level_1' && !values.state) values.state = value;
                    if (addressType === 'postal_code' && !values.zip_code) values.zip_code = value;
                    if (addressType === 'neighborhood') {
                        hasNeighborhoodComponent = true;
                        if (neighborhoodRank < 2) {
                            values.neighborhood = value;
                            neighborhoodRank = 2;
                        }
                    }
                    if ((addressType === 'sublocality' || /^sublocality_level_[1-5]$/.test(addressType)) && neighborhoodRank < 1) {
                        values.neighborhood = value;
                        neighborhoodRank = 1;
                    }
                });
            });

            var searchForm = input.form;
            if (!searchForm) return;
            var setField = function(name, value) {
                var fieldName = 'search[place][' + name + ']';
                var field = searchForm.querySelector('[name="' + fieldName + '"]');
                if (!field) {
                    field = document.createElement('input');
                    field.type = 'hidden';
                    field.name = fieldName;
                    field.dataset.locationSearchPlace = name;
                    searchForm.appendChild(field);
                }
                field.value = value;
            };
            var query = place.formatted_address || input.value;
            var locationField = searchForm.querySelector('[name="search[location]"]');
            if (locationField) locationField.value = query;
            setField('city', values.city);
            setField('state', values.state);
            setField('zip_code', values.zip_code);
            setField('neighborhood', values.neighborhood);
            setField('query', query);
            var placeTypes = place.types || [];
            var isStreetAddress = placeTypes.some(function(type) {
                return ['street_address', 'route', 'premise', 'subpremise', 'intersection'].indexOf(type) !== -1;
            });
            var isColloquialNeighborhood = placeTypes.indexOf('colloquial_area') !== -1 &&
                hasNeighborhoodComponent && !isStreetAddress;
            var kind = placeTypes.some(function(type) {
                return type === 'neighborhood' || type === 'sublocality' || /^sublocality_level_[1-5]$/.test(type);
            }) ? 'neighborhood' : placeTypes.indexOf('postal_code') !== -1 ? 'zip' :
                placeTypes.some(function(type) { return type === 'locality' || type === 'administrative_area_level_3'; }) ? 'city' :
                    isColloquialNeighborhood ? 'neighborhood' : 'address';
            setField('kind', kind);
            if (typeof searchForm.requestSubmit === 'function') searchForm.requestSubmit();
            else searchForm.submit();
        });
    }
}

const loadSearchAutocomplete = (input) => {
  if (!input || input.googlePlacesAutocomplete || input.googlePlacesLoadPromise) return;

  input.googlePlacesLoadPromise = window.loadGoogleMapsApi()
    .then(() => {
      initializeAutocomplete(input);
      if (input.value.trim().length > 0) {
        window.setTimeout(() => input.dispatchEvent(new Event('input', { bubbles: true })), 0);
      }
    })
    .catch((error) => console.error('Unable to load location suggestions.', error))
    .finally(() => {
      input.googlePlacesLoadPromise = null;
    });
};

function fillIn(input) {
  var place = this.getPlace();
  var myInput = $('#' + this.inputId);
  if(place !== undefined && place.address_components.length > 0){
    myInput.addClass('not-empty');
  } else {
    myInput.removeClass('not-empty');
  }
  var facilityUnitId = myInput.data('id');
  for (var i = 0; i < place.address_components.length; i++) {
    var addressType = place.address_components[i].types[0];
    if (componentForm[addressType]) {
    var val = place.address_components[i][componentForm[addressType]];
    var addressType = addressType;
    switch (addressType) {
      case 'locality':
        $('#reservation_request_city_' + facilityUnitId).val(val);
        break;
      case 'administrative_area_level_1':
        $('#reservation_request_state_' + facilityUnitId).val(val);
        break;
      case 'postal_code':
        $('#reservation_request_zip_' + facilityUnitId).val(val);
        break;
      }
    }
  }
  $('#reservation_request_address_' + facilityUnitId).val(place.name);
}

window.setMapAndMarkers = function() {
    var lastMarker = markers[markers.length - 1].attributes;
    var mapOptions = {
        center: new google.maps.LatLng(lastMarker.lat, lastMarker.lng),
        zoom: 11,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_RIGHT
        }
    };
    var map = new google.maps.Map(document.getElementById("map"), mapOptions);
    window.mapInstance = map;
    window.mapLastCenter = mapOptions.center;
    window.mapInitialized = true;
    window.mapMarkersDirty = false;
    window.pendingMapInitialization = false;
    window.mapLoadingHidden = false;
    const ensureOverlayHidden = () => hideMapLoadingOverlay();
    google.maps.event.addListenerOnce(map, 'idle', ensureOverlayHidden);
    google.maps.event.addListenerOnce(map, 'tilesloaded', ensureOverlayHidden);
    window.mapLoadingFallbackTimer = window.setTimeout(ensureOverlayHidden, 2000);

    const backToListViewDiv = document.createElement("div");

    backToListViewControl(backToListViewDiv, map);
    map.controls[google.maps.ControlPosition.TOP_LEFT].push(backToListViewDiv);

    //Create and open InfoWindow.
    var infoWindow = new google.maps.InfoWindow();
    window.activeInfoWindow = infoWindow;
    var infowindowContent = document.querySelector('#infowindow-content');
    infoWindow.setContent(infowindowContent);
    const positionCloseButton = () => {
        const closeButton = document.querySelector('#close-infowindow-button');
        if (!closeButton) {
            return;
        }
        const iwOuter = document.querySelector('.gm-style-iw');
        if (!iwOuter) {
            return;
        }
        const rect = iwOuter.getBoundingClientRect();
        const buttonWidth = closeButton.offsetWidth || 34;
        const buttonHeight = closeButton.offsetHeight || 34;
        const offset = 16;
        const desiredTop = rect.top + window.scrollY - (buttonHeight / 2);
        const desiredLeft = rect.right + window.scrollX - (buttonWidth / 2);

        if (closeButton.parentElement !== document.body) {
            document.body.appendChild(closeButton);
        }
        closeButton.style.position = 'absolute';
        closeButton.style.display = 'block';
        closeButton.style.pointerEvents = 'auto';
        closeButton.style.top = `${desiredTop}px`;
        closeButton.style.left = `${desiredLeft}px`;
    };

    infoWindow.addListener('domready', () => {
        window.requestAnimationFrame(positionCloseButton);
    });
    map.addListener('center_changed', () => {
        window.requestAnimationFrame(positionCloseButton);
    });
    map.addListener('zoom_changed', () => {
        window.requestAnimationFrame(positionCloseButton);
    });
    map.addListener('idle', () => {
        window.requestAnimationFrame(positionCloseButton);
    });
    infoWindow.addListener('closeclick', hideCloseButton);

    for (var i = 0; i < markers.length; i++) {
        var attrs = markers[i].attributes;
        var myLatlng = new google.maps.LatLng(parseFloat(attrs.lat), parseFloat(attrs.lng));
        var marker = new google.maps.Marker({
            position: myLatlng,
            map: map,
            title: attrs.title
        });

        //Attach click event to the marker.
        (function (marker, attrs) {
            google.maps.event.addListener(marker, "click", function (e) {
                // marker.classList.add('marker-link');
                // Wrap the content inside an HTML DIV in order to set height and width of InfoWindow.
                infowindowContent.querySelector(':scope #facility-title').textContent = attrs.title;
                infowindowContent.querySelector(':scope #place-address').textContent = attrs.address;
                infowindowContent.querySelector(':scope #facility-ph').textContent = attrs.ph;
                infowindowContent.querySelector(':scope #place-logo')['src'] = attrs.image;
                let facilityUrlElement = infowindowContent.querySelector(':scope #place-unit-url');
                let facilityUrl = (attrs.facility_url === undefined)? '' : attrs.facility_url;
                if (!infowindowContent.classList.contains('profile-marker')) {
                    facilityUrl = profileResultsLink(facilityUrl, mapResultsStorageType);
                }
                facilityUrlElement['href'] = facilityUrl;
                facilityUrlElement.classList.remove("d-none");
                // if(attrs.facility_url){
                //     facilityUrlElement['href'] = attrs.facility_url;
                //     facilityUrlElement.classList.remove("d-none");
                // } else {
                //     facilityUrlElement.classList.add("d-none");
                // }
                // placeCityState = infowindowContent.querySelector(':scope #place-city-state');
                // if(placeCityState){
                //     placeCityState.textContent = attrs.city + ', ' + attrs.state + ' ' + attrs.zip;
                // }
                let facilityCaption = infowindowContent.querySelector(':scope #facility-caption');
                if(attrs.caption){
                    facilityCaption.style.backgroundColor = attrs.caption.bg_color;
                    facilityCaption.style.color = attrs.caption.text_color;
                    facilityCaption.innerHTML = attrs.caption.body;
                    facilityCaption.classList.remove("d-none");
                }else{
                    facilityCaption.classList.add("d-none");
                }
                if(!infowindowContent.classList.contains('profile-marker')){
                    let facilityDistance = infowindowContent.querySelector(':scope #facility-d');
                    if(attrs.d){
                        facilityDistance.textContent = ' ' + attrs.d + ' Miles away';
                        facilityDistance.classList.remove("d-none");
                    } else {
                        facilityDistance.classList.add("d-none");
                    }
                    infowindowContent.querySelector(':scope #place-phone')['href'] = "tel:"+attrs.ph;
                    let facilityUnits = [];
                    attrs.units.forEach(function(item, index, items) {
                        facilityUnits.push(createLiElement(item.attributes, facilityUrl));
                    });
                    if(facilityUnits.length === 0 && attrs.facility_url){
                        facilityUnits.push("<li><h4>" + attrs.ud + " Unit</h4><h5 class='offers'>" + attrs.uf.join(', ') +
                        "</h5><a href='"+ facilityUrl +"' class='price-info'>$" + attrs.up +
                        "<i class='ml-2'>></i></a></li>")
                    }
                    infowindowContent.querySelector(':scope #facility_units_listing').innerHTML = facilityUnits.join('');
                }
                changeCloseButtonText();
                infoWindow.open(map, marker);
            });
            if(markers.length === 1){
                google.maps.event.trigger(marker, 'click');
                // marker.setIcon({
                //     url: document.getElementById('icon-map-marker')['src'],
                //     scaledSize: new google.maps.Size(35, 45)
                // });
            }
        })(marker, attrs);
    }
}
function changeCloseButtonText(){
    setTimeout(function() {
        let closeButton = document.querySelector('[aria-label="Close"]');
        if(closeButton){
            closeButton.textContent = "X";
        }
    }, 200);
}

function createLiElement(data, facility_url){
    const special = (data.special_discount && data.special_discount.trim().length > 0)
      ? data.special_discount
      : 'ACT FAST! LIMITED UNITS';
    return "<li><h4>" + data.dimension + " Unit</h4><h5 class='offers'>" + special +
    "</h5><a href='" + facility_url + "' class='price-info'>$" + data.price + " <i class='ml-2'>></i></a></li>"
}

window.geolocate = function() {
    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(function(position) {
            window.location.href = '/city?search[lat]=' + position.coords.latitude + '&search[lng]=' + position.coords.longitude;
        });
    }
}

$(function() {
    setupLocationSearch(document.getElementById('search_address'));

    $(document).on('click', '#close-infowindow-button', function(event) {
        event.preventDefault();
        if (window.activeInfoWindow) {
            window.activeInfoWindow.close();
        }
        hideCloseButton();
    });

    $(document).on('click', "[data-behavior='close-infowindow']", function(event){
        event.preventDefault();
        if (window.activeInfoWindow) {
            window.activeInfoWindow.close();
        }
    });

  $(".cmprPrice").click(function(){
    openMapModal();
  });

  $(document).on('keydown', function(event) {
    if ((event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27)) {
      if ($("#cmprPriceModal").hasClass('open-map')) {
        event.preventDefault();
        closeMapModal();
      }
    }
  });

  $(document).on('click', ".orange-btn.btn[href=''], #infowindow-content .price-info[href='']", function(event){
    event.preventDefault();
    closeMapModal();
  });
});
